import { useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Button, Icon, Input, Modal, Notification, Select } from "@stellar/design-system";

import { DirectPaymentConfirmation } from "@/components/DirectPaymentConfirmation/DirectPaymentConfirmation";
import { ErrorWithExtras } from "@/components/ErrorWithExtras";
import { SelectedReceiverInfo } from "@/components/SelectedReceiverInfo/SelectedReceiverInfo";

import { directPayment } from "@/constants/directPayment";

import { useAllAssets } from "@/apiQueries/useAllAssets";
import { useReceiversReceiverId } from "@/apiQueries/useReceiversReceiverId";
import { useSearchReceivers } from "@/apiQueries/useSearchReceivers";
import { useWallets } from "@/apiQueries/useWallets";

import { getEnhancedWalletErrorMessage } from "@/helpers/walletErrorMessages";
import { isValidWalletAddress } from "@/helpers/walletValidate";

import { useDebounce } from "@/hooks/useDebounce";
import { usePrevious } from "@/hooks/usePrevious";

import { ApiAssetWithTrustline, ApiReceiver, CreateDirectPaymentRequest } from "@/types";

import "./styles.scss";

interface DirectPaymentCreateModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (paymentData: CreateDirectPaymentRequest) => void;
  onResetQuery: () => void;
  isLoading: boolean;
  errorMessage?: string;
  /**
   * When set, the receiver is fixed to this ID (no search): used when the modal is
   * opened from a receiver's page, so the receiver never has to be re-entered.
   */
  prefilledReceiverId?: string;
}

// Best-effort display label for a receiver when there is no search query to match against.
const getReceiverDisplayName = (receiver: ApiReceiver): string =>
  receiver.phone_number || receiver.email || receiver.external_id || receiver.id;

const INITIAL_FORM_DATA = {
  assetId: "",
  amount: "",
  receiverSearch: "",
  selectedReceiver: null as ApiReceiver | null,
  walletId: "",
  externalPaymentId: "",
};

export const DirectPaymentCreateModal: React.FC<DirectPaymentCreateModalProps> = ({
  visible,
  onClose,
  onSubmit,
  onResetQuery,
  isLoading,
  errorMessage,
  prefilledReceiverId,
}) => {
  const isReceiverLocked = Boolean(prefilledReceiverId);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentDataToConfirm, setPaymentDataToConfirm] =
    useState<CreateDirectPaymentRequest | null>(null);
  const [selectedReceiverId, setSelectedReceiverId] = useState<string | undefined>(
    prefilledReceiverId,
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedReceiverId(prefilledReceiverId);
    // Drop the receiver loaded for the previous ID (and the wallet picked for it) so it
    // can't be paid in the window before the new receiver's details arrive.
    setFormData((prev) =>
      prev.selectedReceiver
        ? { ...prev, receiverSearch: "", selectedReceiver: null, walletId: "" }
        : prev,
    );
  }, [prefilledReceiverId]);

  const queryClient = useQueryClient();
  const debouncedReceiverSearch = useDebounce(
    formData.receiverSearch,
    directPayment.SEARCH_DEBOUNCE_MS,
  );
  const previousVisible = usePrevious(visible);
  const { data: allAssets } = useAllAssets({ enabled: true });

  // Fetch full receiver details including verifications when a receiver is selected
  // Using dataFormat 'raw' to get raw API response without formatting
  const {
    data: receiverDetailsRaw,
    error: receiverDetailsError,
    isFetching: isFetchingReceiverDetails,
    refetch: refetchReceiverDetails,
  } = useReceiversReceiverId<ApiReceiver>({
    receiverId: selectedReceiverId,
    dataFormat: "raw",
  });
  const selectedAsset = useMemo(
    () =>
      allAssets?.find((asset) => asset.id === formData.assetId) as
        | ApiAssetWithTrustline
        | undefined,
    [allAssets, formData.assetId],
  );
  // Fetch wallets - for verified receivers, explicitly get only SEP-24 wallets
  const isReceiverVerified = Boolean(formData.selectedReceiver?.verifications?.length);
  const { data: supportedWallets = [], isLoading: walletsLoading } = useWallets({
    supportedAssets: selectedAsset ? [selectedAsset.code] : [],
    // For verified receivers, only get SEP-24 wallets (user_managed = false)
    // For non-verified, get all wallets (undefined = no filter)
    userManaged: isReceiverVerified ? false : undefined,
  });
  const { data: searchResults, isLoading: searchLoading } = useSearchReceivers(
    debouncedReceiverSearch,
    !isReceiverLocked,
  );
  const filteredWallets = useMemo(() => {
    if (!supportedWallets.length || !formData.assetId || !formData.selectedReceiver) {
      return [];
    }

    const receiverWalletIds = formData.selectedReceiver.wallets.map((w) => w.wallet.id);
    const isReceiverVerified = Boolean(formData.selectedReceiver.verifications?.length);

    // For verified receivers with SEP-24 wallets, show all enabled SEP-24 wallets
    // For regular receivers, only show pre-registered wallets
    if (isReceiverVerified) {
      return supportedWallets.filter((wallet) => wallet.enabled && !wallet.user_managed);
    }

    return supportedWallets.filter(
      (wallet) => wallet.enabled && receiverWalletIds.includes(wallet.id),
    );
  }, [supportedWallets, formData.assetId, formData.selectedReceiver]);

  const isWalletFieldLoading = walletsLoading && Boolean(formData.selectedReceiver);
  const isReceiverSearchLoading =
    searchLoading && debouncedReceiverSearch.trim().length >= directPayment.SEARCH_MIN_CHARS;
  const getWalletFieldNote = (): string => {
    if (!formData.selectedReceiver) return "Select a receiver first";
    if (isWalletFieldLoading) return "Loading compatible wallets…";
    if (filteredWallets.length === 0) return "No compatible wallets available for this receiver";

    const isReceiverVerified = Boolean(formData.selectedReceiver.verifications?.length);
    if (isReceiverVerified) {
      const registeredCount = filteredWallets.filter((w) => {
        const rw = formData.selectedReceiver?.wallets?.find((wr) => wr.wallet.id === w.id);
        return rw && rw.stellar_address;
      })?.length;

      if (registeredCount === filteredWallets.length) {
        return `${filteredWallets.length} wallet(s) available`;
      } else {
        return `${filteredWallets.length} wallet(s) available - Not Registered wallets will receive an invitation after payment creation`;
      }
    }
    return `${filteredWallets.length} compatible wallet(s) available`;
  };

  const isReceiverWalletAddress =
    !isReceiverLocked && isValidWalletAddress(formData.receiverSearch);

  const isAmountValid =
    formData.amount.trim() !== "" && !isNaN(Number(formData.amount)) && Number(formData.amount) > 0;
  const isReceiverValid =
    Boolean(formData.selectedReceiver) ||
    (formData.receiverSearch.trim() !== "" && isReceiverWalletAddress);
  const isWalletValid = isReceiverWalletAddress ? true : Boolean(formData.walletId);
  const isSubmitEnabled = Boolean(
    formData.assetId && isAmountValid && isReceiverValid && isWalletValid,
  );

  useEffect(() => {
    if (previousVisible && !visible) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData(INITIAL_FORM_DATA);
      setFormErrors({});
      setShowConfirmation(false);
      setPaymentDataToConfirm(null);
      setSelectedReceiverId(prefilledReceiverId);
    }
  }, [visible, previousVisible, prefilledReceiverId]);

  useEffect(() => {
    if (!receiverDetailsRaw) return;

    // Both branches decide against `prev` rather than the values captured when this
    // effect was queued: the receiver can be cleared (a switch to another receiver)
    // between the render and this run, and a stale decision would revive it.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFormData((prev) => {
      const currentReceiver = prev.selectedReceiver;

      if (currentReceiver && currentReceiver.id === receiverDetailsRaw.id) {
        return {
          ...prev,
          selectedReceiver: {
            ...currentReceiver,
            verifications: receiverDetailsRaw.verifications || [],
          },
        };
      }

      if (isReceiverLocked && receiverDetailsRaw.id === prefilledReceiverId) {
        // Receiver came from the page we were opened on, so select it without a search.
        return {
          ...prev,
          receiverSearch: getReceiverDisplayName(receiverDetailsRaw),
          selectedReceiver: receiverDetailsRaw,
        };
      }

      return prev;
    });
  }, [receiverDetailsRaw, formData.selectedReceiver?.id, isReceiverLocked, prefilledReceiverId]);

  useEffect(() => {
    if (filteredWallets.length > 0 && !formData.walletId) {
      const viaWallet = filteredWallets.find((w) => w.name === "VIA Wallet");
      if (viaWallet) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFormData((prev) => ({ ...prev, walletId: viaWallet.id }));
      } else if (filteredWallets.length === 1) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFormData((prev) => ({ ...prev, walletId: filteredWallets[0].id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredWallets]);

  const handleClose = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
      setPaymentDataToConfirm(null);
    } else {
      onClose();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (errorMessage) {
      onResetQuery();
    }

    const { id, value } = event.target;

    if (formErrors[id]) {
      setFormErrors((prev) => ({ ...prev, [id]: "" }));
    }

    if (id === "assetId") {
      setFormData((prev) => ({
        ...prev,
        assetId: value,
        // A locked receiver stays selected: only the wallet depends on the asset.
        ...(isReceiverLocked ? {} : { receiverSearch: "", selectedReceiver: null }),
        walletId: "",
      }));
      setFormErrors((prev) => ({
        ...prev,
        receiverSearch: "",
        walletId: "",
      }));
      // Clear any cached receiver search results when changing asset or clearing the field
      queryClient.removeQueries({ queryKey: ["receivers", "search"] });
      return;
    }

    if (id === "receiverSearch") {
      const shouldClearReceiver = formData.selectedReceiver && value !== formData.receiverSearch;

      setFormData((prev) => ({
        ...prev,
        receiverSearch: value,
        selectedReceiver: shouldClearReceiver ? null : prev.selectedReceiver,
        walletId: "",
      }));

      if (shouldClearReceiver) {
        setSelectedReceiverId(undefined);
      }

      setFormErrors((prev) => ({ ...prev, walletId: "" }));
      if (value.trim().length < directPayment.SEARCH_MIN_CHARS) {
        // Proactively remove cached results so we don't show stale data
        queryClient.removeQueries({ queryKey: ["receivers", "search"] });
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleReceiverSelect = (receiver: ApiReceiver) => {
    setSelectedReceiverId(receiver.id);

    setFormData((prev) => ({
      ...prev,
      receiverSearch: getReceiverDisplayInfo(receiver, formData.receiverSearch),
      selectedReceiver: receiver,
      walletId: "",
    }));
    setFormErrors((prev) => ({ ...prev, receiverSearch: "" }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    const isAddr = isReceiverWalletAddress;
    const selectedWallet = supportedWallets.find((w) => w.id === formData.walletId);

    const paymentData: CreateDirectPaymentRequest = {
      amount: formData.amount.trim(),
      asset: {
        id: selectedAsset?.id,
        code: selectedAsset?.code,
        issuer: selectedAsset?.issuer,
      },
      receiver: isAddr
        ? { wallet_address: formData.receiverSearch.trim() }
        : { id: formData.selectedReceiver!.id },
      ...(isAddr
        ? { wallet: { address: formData.receiverSearch.trim() } }
        : selectedWallet && { wallet: { id: selectedWallet.id } }),
      ...(formData.externalPaymentId.trim() && {
        external_payment_id: formData.externalPaymentId.trim(),
      }),
    };

    setPaymentDataToConfirm(paymentData);
    setShowConfirmation(true);
  };

  const handleConfirmPayment = () => {
    if (paymentDataToConfirm) {
      onSubmit(paymentDataToConfirm);
    }
  };

  const getReceiverDisplayInfo = (receiver: ApiReceiver, searchQuery: string): string => {
    const query = searchQuery.toLowerCase();
    if (receiver.email?.toLowerCase().includes(query)) return receiver.email;
    if (receiver.phone_number?.includes(query)) return receiver.phone_number;
    if (receiver.external_id?.toLowerCase().includes(query)) return receiver.external_id;
    return receiver.phone_number || receiver.email || receiver.external_id || "";
  };

  const showReceiverField = isReceiverLocked || Boolean(formData.assetId);
  const showWalletField = formData.assetId && !isReceiverWalletAddress;

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.assetId) errors.assetId = "Asset selection is required";
    if (!formData.amount.trim()) {
      errors.amount = "Amount is required";
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      errors.amount = "Amount must be a valid positive number";
    }
    if (isReceiverLocked) {
      if (!formData.selectedReceiver) {
        errors.receiverSearch = "Receiver details could not be loaded. Please try again.";
      }
    } else if (!formData.receiverSearch.trim()) {
      errors.receiverSearch = "Receiver is required";
    } else if (!isReceiverWalletAddress && !formData.selectedReceiver) {
      errors.receiverSearch =
        "Please select a receiver from search results or enter a valid wallet address";
    }
    if (!isReceiverWalletAddress && !formData.walletId) {
      errors.walletId = "Wallet selection is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const renderSearchResults = () => {
    if (!formData.receiverSearch || formData.selectedReceiver) return null;

    if (isReceiverSearchLoading) {
      return (
        <div className="DirectPaymentCreateModal__searchResults">
          <div className="DirectPaymentCreateModal__searchResult">Searching...</div>
        </div>
      );
    }

    if (
      !isReceiverWalletAddress &&
      formData.receiverSearch &&
      debouncedReceiverSearch.trim().length >= directPayment.SEARCH_MIN_CHARS &&
      searchResults?.data?.length === 0
    ) {
      return (
        <div className="DirectPaymentCreateModal__noResults">
          No receivers found. Make sure the receiver exists or enter a wallet address.
        </div>
      );
    }

    if (
      debouncedReceiverSearch.trim().length >= directPayment.SEARCH_MIN_CHARS &&
      searchResults?.data &&
      searchResults.data.length > 0
    ) {
      return (
        <div className="DirectPaymentCreateModal__searchResults">
          <div className="DirectPaymentCreateModal__searchResults__content">
            {searchResults.data.slice(0, directPayment.MAX_SEARCH_RESULTS).map((receiver) => {
              const isSelected = formData.selectedReceiver?.id === receiver.id;
              return (
                <div
                  key={receiver.id}
                  className="DirectPaymentCreateModal__searchResult"
                  onClick={() => handleReceiverSelect(receiver)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleReceiverSelect(receiver);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select receiver ${getReceiverDisplayInfo(receiver, formData.receiverSearch)}`}
                >
                  <div>
                    <div className="DirectPaymentCreateModal__searchResultMain">
                      {getReceiverDisplayInfo(receiver, formData.receiverSearch)}
                    </div>
                    <div className="DirectPaymentCreateModal__searchResultSub">
                      ID: {receiver.id.slice(0, directPayment.RECEIVER_ID_PREVIEW_LENGTH)}… •{" "}
                      {receiver.wallets.length} wallet(s)
                    </div>
                  </div>
                  {isSelected && <Icon.CheckCircle />}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderWalletOptions = () => {
    return filteredWallets.map((wallet) => {
      const recvWallet = formData.selectedReceiver?.wallets?.find((w) => w.wallet.id === wallet.id);
      const addr = recvWallet?.stellar_address || "";
      const shortAddr = addr && `${addr.slice(0, 4)}…${addr.slice(-4)}`;
      const isRegistered = Boolean(recvWallet && addr);

      // Show registration status and address based on wallet state
      let label: string;
      if (isRegistered) {
        label = `${wallet.name} - Registered (${shortAddr})`;
      } else if (recvWallet) {
        // Wallet exists but no address yet - in registration process
        label = `${wallet.name} - Not Registered (pending)`;
      } else {
        // Will be auto-registered for verified receivers
        const isVerified = Boolean(formData.selectedReceiver?.verifications?.length);
        if (isVerified && !wallet.user_managed) {
          label = `${wallet.name} - Not Registered`;
        } else {
          label = `${wallet.name}`;
        }
      }

      return (
        <option key={wallet.id} value={wallet.id}>
          {label}
        </option>
      );
    });
  };

  return (
    <Modal visible={visible} onClose={handleClose}>
      <Modal.Heading>
        {showConfirmation ? "Confirm Direct Payment" : "Create A Direct Payment"}
      </Modal.Heading>
      {showConfirmation ? (
        <>
          <Modal.Body>
            {errorMessage && (
              <Notification variant="error" title="Error" isFilled={true}>
                <ErrorWithExtras
                  appError={{ message: getEnhancedWalletErrorMessage(errorMessage) }}
                />
              </Notification>
            )}
            {paymentDataToConfirm && (
              <DirectPaymentConfirmation
                paymentData={paymentDataToConfirm}
                selectedReceiver={formData.selectedReceiver}
                receiverSearch={formData.receiverSearch}
                selectedWalletInfo={supportedWallets.find((w) => w.id === formData.walletId)}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              size="md"
              variant="tertiary"
              onClick={() => setShowConfirmation(false)}
              disabled={isLoading}
            >
              Back
            </Button>
            <Button
              size="md"
              variant="primary"
              onClick={handleConfirmPayment}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Confirm Payment
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <form onSubmit={handleSubmit} onReset={handleClose}>
          <Modal.Body>
            <div className="DirectPaymentCreateModal__description">
              {isReceiverLocked
                ? "Send a single payment directly to this receiver."
                : "Send a single payment directly to a receiver via email, phone number, or wallet address."}
            </div>
            {errorMessage && (
              <Notification variant="error" title="Error" isFilled={true}>
                <ErrorWithExtras
                  appError={{ message: getEnhancedWalletErrorMessage(errorMessage) }}
                />
              </Notification>
            )}

            <div className="DirectPaymentCreateModal__form">
              {/* Asset Selection */}
              <Select
                fieldSize="sm"
                id="assetId"
                label="Asset"
                value={formData.assetId}
                onChange={handleInputChange}
                error={formErrors.assetId}
                note={
                  selectedAsset &&
                  `Available balance: ${selectedAsset?.balance} ${selectedAsset?.code}`
                }
                required
              >
                <option value="">Select asset</option>
                {allAssets?.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.code}{" "}
                    {asset.code !== "XLM" &&
                      `(${asset.issuer.slice(0, directPayment.ASSET_ISSUER_PREVIEW_LENGTH / 2)}…${asset.issuer.slice(-directPayment.ASSET_ISSUER_PREVIEW_LENGTH / 2)})`}
                  </option>
                ))}
              </Select>

              {/* Amount */}
              <Input
                fieldSize="sm"
                id="amount"
                name="amount"
                type="text"
                label="Amount"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={handleInputChange}
                error={formErrors.amount}
                rightElement={
                  selectedAsset?.code ? (
                    <span className="DirectPaymentCreateModal__currency">{selectedAsset.code}</span>
                  ) : undefined
                }
                disabled={!formData.assetId}
                required
              />

              {/* Receiver: locked to the receiver we were opened from, or searchable */}
              <div
                className="DirectPaymentCreateModal__fieldWrapper"
                data-visible={showReceiverField}
              >
                <div className="DirectPaymentCreateModal__fieldWrapper__content">
                  {isReceiverLocked ? (
                    <div className="DirectPaymentCreateModal__lockedReceiver">
                      <div className="DirectPaymentCreateModal__lockedReceiver__label">
                        Receiver
                      </div>
                      {formData.selectedReceiver ? (
                        <SelectedReceiverInfo receiver={formData.selectedReceiver} />
                      ) : receiverDetailsError ? (
                        <div className="DirectPaymentCreateModal__lockedReceiver__error">
                          <ErrorWithExtras appError={receiverDetailsError} />
                          <Button
                            size="sm"
                            variant="tertiary"
                            type="button"
                            onClick={() => refetchReceiverDetails()}
                            isLoading={isFetchingReceiverDetails}
                          >
                            Try again
                          </Button>
                        </div>
                      ) : (
                        <div className="DirectPaymentCreateModal__lockedReceiver__loading">
                          Loading receiver details…
                        </div>
                      )}
                      {formErrors.receiverSearch && (
                        <span className="DirectPaymentCreateModal__error">
                          {formErrors.receiverSearch}
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      <Input
                        fieldSize="sm"
                        id="receiverSearch"
                        label="Receiver"
                        placeholder="Search by email, phone, or enter wallet address (GXXX...)"
                        infoText="Search results appear after entering at least 3 characters"
                        tooltipPlacement="top-start"
                        value={formData.receiverSearch}
                        onChange={handleInputChange}
                        error={formErrors.receiverSearch}
                        note={isReceiverWalletAddress && "Wallet address detected"}
                        required
                      />

                      {formData.selectedReceiver && !isReceiverWalletAddress && (
                        <SelectedReceiverInfo receiver={formData.selectedReceiver} />
                      )}

                      {renderSearchResults()}
                    </>
                  )}
                </div>
              </div>

              {/* Wallet Selection */}
              <div
                className="DirectPaymentCreateModal__fieldWrapper"
                data-visible={showReceiverField && showWalletField}
              >
                <div className="DirectPaymentCreateModal__fieldWrapper__content">
                  <Select
                    fieldSize="sm"
                    id="walletId"
                    label="Wallet"
                    value={formData.walletId}
                    onChange={handleInputChange}
                    error={formErrors.walletId}
                    disabled={filteredWallets.length <= 1}
                    note={getWalletFieldNote()}
                    required
                  >
                    <option value="">Select wallet</option>
                    {renderWalletOptions()}
                  </Select>
                </div>
              </div>

              {/* External Payment ID */}
              <div
                className="DirectPaymentCreateModal__fieldWrapper"
                data-visible={showReceiverField}
              >
                <div className="DirectPaymentCreateModal__fieldWrapper__content">
                  <Input
                    fieldSize="sm"
                    id="externalPaymentId"
                    name="externalPaymentId"
                    type="text"
                    infoText="An identifier for the payment defined by your organization"
                    tooltipPlacement="top-start"
                    label="Enter external payment ID (optional)"
                    placeholder="Enter external payment ID"
                    value={formData.externalPaymentId}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button size="md" variant="tertiary" type="reset" disabled={isLoading}>
              Cancel
            </Button>
            <Button
              size="md"
              variant="primary"
              type="submit"
              disabled={!isSubmitEnabled}
              isLoading={isLoading}
            >
              Review
            </Button>
          </Modal.Footer>
        </form>
      )}
    </Modal>
  );
};
